import React, { useRef, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import CardCommon from '../CardCommon';
import type { CardCommonType } from '../CardCommon';
import useChartOptions, {
  ChartOptionsType,
} from '../../hooks/useChartOptions/useChartOptions';
import { Justify } from 'tea-component';
import './index.scss';
type EchartsBoxType = {
  height?: number;
  loadingHeight?: number;
  leftTitle?: string | JSX.Element | null;
  rightTitle?: string | JSX.Element | null;
  className?: string;
} & Omit<CardCommonType, 'children'>;

type EchartsType = EchartsBoxType & {
  options: any;
  lazyUpdate?: boolean;
  eventsDict?: any;
  optionsProps?: Omit<ChartOptionsType, 'userOptions'>;
  onChartReady?: (value: any) => void;
  isMarketing?: boolean;
};

function EChartCommonNoForwardRef(props: Omit<EchartsType, 'ref'>, ref: any) {
  const {
    options,
    height = 300,
    lazyUpdate = false,
    eventsDict = {},
    optionsProps = {},
    onChartReady = () => {}, 
    ...otherProps
  } = props as EchartsType;

  // 用于追踪组件是否已挂载
  const isMountedRef = useRef(true);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // 组件卸载时销毁 ECharts 实例，防止内存泄漏和 null 引用错误
      if (chartRef.current) {
        try {
          const instance = chartRef.current.getEchartsInstance?.();
          if (instance) {
            instance.dispose();
          }
        } catch (e) {
          // 忽略卸载时的错误
        }
      }
    };
  }, []);

  const mergeOptions = useChartOptions({
    userOptions: options,
    ...optionsProps,
  });

  // 安全的 onChartReady 包装，确保组件仍然挂载
  const handleChartReady = useCallback((instance: any) => {
    if (isMountedRef.current && onChartReady) {
      onChartReady(instance);
    }
  }, [onChartReady]);

  // console.log(mergeOptions, 'mergeOptions')
  return (
    <EchartsCommonBox height={height} {...otherProps}>
      <ReactECharts
        option={mergeOptions}
        style={{ height }}
        notMerge={true}
        lazyUpdate={lazyUpdate}
        onEvents={eventsDict}
        ref={(r) => {
          chartRef.current = r;
          if (typeof ref === 'function') {
            ref(r);
          } else if (ref) {
            ref.current = r;
          }
        }}
        onChartReady={handleChartReady}
      />
    </EchartsCommonBox>
  );
}

/**
 * TODO: 其他系统使用
 * EChart组件
 */
const EChartCommon = React.forwardRef<any, EchartsType>(
  EChartCommonNoForwardRef,
);

export function EchartsCommonBox({
  className,
  loadingHeight,
  height,
  emptyHeight,
  leftTitle,
  rightTitle,
  children,
  ...otherProps
}: EchartsBoxType & { children: any }) {
  function getTitleInfo(title: any) {
    if (!title) return null;

    return <div className="dbo-echart-common-title">{title}</div>;
  }

  function titleElement() {
    if (leftTitle || rightTitle) {
      return (
        <Justify
          className="dbo-chart-title-info"
          left={getTitleInfo(leftTitle)}
          right={getTitleInfo(rightTitle)}
        ></Justify>
      );
    }

    return null;
  }

  return (
    <div style={{ width: '100%' }} className={className}>
      <CardCommon
        contentCard
        loadingHeight={loadingHeight || height}
        emptyHeight={emptyHeight || height}
        {...otherProps}
      >
        {titleElement()}
        {children}
      </CardCommon>
    </div>
  );
}

export default EChartCommon;

/**
 * TODO: 其他系统使用
 * 统一图表展示高亮x轴数据 + 并处理hover线相关样式
 * @param func
 * @param otherOptions
 * @param otherLabelOptions
 * @returns
 */
export function getAxisPointer(
  func: (value: any) => string,
  otherOptions: { [key: string]: any } = {},
  otherLabelOptions: { [key: string]: any } = {},
) {
  return {
    label: {
      formatter: func,
      ...otherLabelOptions,
    },
    ...otherOptions,
  };
}
